import { useAuth0 } from '@auth0/auth0-react';
import {
  CopyIcon,
  InfoIcon,
  RepeatIcon,
  WarningIcon,
} from '@chakra-ui/icons';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Code,
  Divider,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  IconButton,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import { getDeploymentIdentifier } from '../../utils/deploymentVersion';
import { isNonProdEnv } from '../../utils/env';
import { getUserPermissions } from '../../utils/permissionUtils';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth0();
  const {
    accessToken,
    decoded,
    isAuthenticated,
    refreshAccessToken,
  } = useAuth();
  const permissions = getUserPermissions();
  const tokenInfo = decoded;
  const isTokenValid = useMemo(() => {
    if (!isAuthenticated || !accessToken || !decoded) return false;
    if (decoded.exp && decoded.exp * 1000 < Date.now()) return false;
    return true;
  }, [accessToken, decoded, isAuthenticated]);
  const [deploymentInfo, setDeploymentInfo] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const isDevEnvironment = isNonProdEnv();

  useEffect(() => {
    // Get deployment information
    setDeploymentInfo(getDeploymentIdentifier());
  }, []);

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  const handleRefreshToken = async () => {
    setIsRefreshing(true);

    try {
      // Show loading state
      toast({
        title: 'Refreshing login...',
        description:
          'Please wait while we update your authentication',
        status: 'info',
        duration: 2000,
        isClosable: false,
      });

      const token = await refreshAccessToken({ forceRefresh: true });
      if (!token) {
        throw new Error('Unable to refresh token');
      }

      toast({
        title: 'Authentication refreshed',
        description: 'Your login has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      toast({
        title: 'Refresh failed',
        description: 'Please sign out and sign back in',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: `${label} copied`,
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  if (!user) {
    return (
      <Box p="4">
        <Text>Loading...</Text>
      </Box>
    );
  }

  const getPermissionBadgeColor = (hasPermission: boolean) => {
    return hasPermission ? 'green' : 'gray';
  };

  const getTokenStatusColor = () => {
    return isTokenValid ? 'green' : 'red';
  };

  const getTokenStatusText = () => {
    return isTokenValid ? 'Active' : 'Needs Attention';
  };

  const getTokenStatusDescription = () => {
    return isTokenValid
      ? 'Your login is working properly'
      : 'There may be an issue with your login. Try refreshing or signing out and back in.';
  };

  return (
    <Box p="6" maxW="1200px" mx="auto">
      {/* Header Section */}
      <Box mb="8">
        <Heading as="h1" size="xl" mb="2">
          My Account & Settings
        </Heading>
        <Text color="gray.600">
          Manage your account and resolve any login issues
        </Text>
      </Box>

      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap="6">
        {/* Left Column - User Info & Account Settings */}
        <GridItem>
          <Stack spacing="6">
            {/* User Profile Card */}
            <Card>
              <CardHeader>
                <Heading size="md">Profile Information</Heading>
              </CardHeader>
              <CardBody>
                <Flex alignItems="center" mb="4">
                  <Image
                    src={user.picture}
                    alt="Profile"
                    borderRadius="full"
                    boxSize="80px"
                    mr="4"
                  />
                  <Box>
                    <Heading size="md" mb="1">
                      {user.name}
                    </Heading>
                    <Text color="gray.600" mb="2">
                      {user.email}
                    </Text>
                    <Badge colorScheme="blue" variant="subtle">
                      {user.email_verified
                        ? 'Email Verified'
                        : 'Email Not Verified'}
                    </Badge>
                  </Box>
                </Flex>

                <Divider my="4" />

                <VStack spacing="3" align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">User ID:</Text>
                    <HStack>
                      <Code fontSize="sm">
                        {user.sub
                          ? `${user.sub.substring(0, 8)}...`
                          : 'Not Available'}
                      </Code>
                      {user.sub && (
                        <Tooltip label="Copy User ID">
                          <IconButton
                            size="sm"
                            icon={<CopyIcon />}
                            aria-label="Copy User ID"
                            onClick={() =>
                              copyToClipboard(
                                user.sub || '',
                                'User ID'
                              )
                            }
                          />
                        </Tooltip>
                      )}
                    </HStack>
                  </HStack>

                  <HStack justify="space-between">
                    <Text fontWeight="medium">Last Updated:</Text>
                    <Text>
                      {user.updated_at
                        ? new Date(
                            user.updated_at
                          ).toLocaleDateString()
                        : 'Not Available'}
                    </Text>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Authentication Status Card */}
            <Card>
              <CardHeader>
                <Heading size="md">Login Status</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing="4" align="stretch">
                  {/* Status Overview */}
                  <Box
                    textAlign="center"
                    p="4"
                    bg="gray.50"
                    borderRadius="md"
                  >
                    <Stat>
                      <StatLabel>Current Status</StatLabel>
                      <StatNumber>
                        <Badge
                          colorScheme={getTokenStatusColor()}
                          fontSize="1rem"
                          p="2"
                          borderRadius="md"
                        >
                          {getTokenStatusText()}
                        </Badge>
                      </StatNumber>
                      <StatHelpText>
                        {getTokenStatusDescription()}
                      </StatHelpText>
                    </Stat>
                  </Box>

                  <Divider />

                  {/* Quick Actions */}
                  <Text fontWeight="medium" mb="2">
                    Quick Actions:
                  </Text>

                  <SimpleGrid columns={1} spacing="3">
                    <Button
                      size="md"
                      colorScheme="blue"
                      variant="outline"
                      onClick={handleRefreshToken}
                      leftIcon={<RepeatIcon />}
                      width="full"
                      isLoading={isRefreshing}
                    >
                      Refresh My Login
                    </Button>

                    <Button
                      size="md"
                      colorScheme="orange"
                      variant="outline"
                      onClick={handleLogout}
                      leftIcon={<WarningIcon />}
                      width="full"
                    >
                      Sign Out and Sign In Again
                    </Button>
                  </SimpleGrid>

                  {/* Help Text */}
                  <Alert status="info" variant="subtle">
                    <AlertIcon />
                    <Box>
                      <AlertTitle>Having trouble?</AlertTitle>
                      <AlertDescription>
                        If you can't see practices or providers, try
                        refreshing your login first. If that doesn't
                        work, use the reset option and sign back in.
                      </AlertDescription>
                    </Box>
                  </Alert>
                </VStack>
              </CardBody>
            </Card>

            {/* Account Actions Card */}
            <Card>
              <CardHeader>
                <Heading size="md">Account Actions</Heading>
              </CardHeader>
              <CardBody>
                <Button
                  colorScheme="red"
                  variant="outline"
                  onClick={handleLogout}
                  width="full"
                  size="lg"
                >
                  Sign Out
                </Button>
              </CardBody>
            </Card>
          </Stack>
        </GridItem>

        {/* Right Column - Permissions & Technical Info */}
        <GridItem>
          <Stack spacing="6">
            {/* Permissions Card */}
            <Card>
              <CardHeader>
                <Heading size="md">Your Access Rights</Heading>
              </CardHeader>
              <CardBody>
                <VStack spacing="4" align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Role:</Text>
                    <Badge colorScheme="purple" variant="subtle">
                      {permissions.role || 'Not Set'}
                    </Badge>
                  </HStack>

                  <Divider />

                  <Text fontWeight="medium" mb="2">
                    What you can do:
                  </Text>

                  <VStack spacing="3" align="stretch">
                    <HStack justify="space-between">
                      <Text>Daily Notes:</Text>
                      <Badge
                        colorScheme={getPermissionBadgeColor(
                          permissions.canEditDailyNotes
                        )}
                      >
                        {permissions.canEditDailyNotes
                          ? 'Can Edit'
                          : 'View Only'}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between">
                      <Text>Office Alerts:</Text>
                      <Badge
                        colorScheme={getPermissionBadgeColor(
                          permissions.canEditOfficeAlerts
                        )}
                      >
                        {permissions.canEditOfficeAlerts
                          ? 'Can Edit'
                          : 'View Only'}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between">
                      <Text>Insurance Information:</Text>
                      <Badge
                        colorScheme={getPermissionBadgeColor(
                          permissions.canEditInsurance
                        )}
                      >
                        {permissions.canEditInsurance
                          ? 'Can Edit'
                          : 'View Only'}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between">
                      <Text>Admin Access:</Text>
                      <Badge
                        colorScheme={getPermissionBadgeColor(
                          permissions.isAdmin
                        )}
                      >
                        {permissions.isAdmin ? 'Yes' : 'No'}
                      </Badge>
                    </HStack>
                  </VStack>
                </VStack>
              </CardBody>
            </Card>

            {/* Technical Information Card - Only show in QA */}
            {isDevEnvironment && (
              <Card>
                <CardHeader>
                  <Heading size="md">
                    Technical Information (QA Only)
                  </Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing="4" align="stretch">
                    <HStack justify="space-between">
                      <Text fontWeight="medium">Environment:</Text>
                      <Badge colorScheme="orange">
                        {import.meta.env.MODE}
                      </Badge>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontWeight="medium">App Version:</Text>
                      <Text fontSize="sm">
                        {import.meta.env.VITE_APP_VERSION ||
                          'Not Set'}
                      </Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontWeight="medium">Build Time:</Text>
                      <Text fontSize="sm">
                        {import.meta.env.VITE_BUILD_TIME || 'Not Set'}
                      </Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontWeight="medium">Deployment:</Text>
                      <Text fontSize="sm">{deploymentInfo}</Text>
                    </HStack>

                    <HStack justify="space-between">
                      <Text fontWeight="medium">
                        Client ID Practice:
                      </Text>
                      <Text fontSize="sm">
                        {import.meta.env.VITE_CLIENT_ID_PRACTICE ||
                          'Not Set'}
                      </Text>
                    </HStack>

                    <Button
                      size="sm"
                      colorScheme="blue"
                      variant="outline"
                      onClick={onOpen}
                      leftIcon={<InfoIcon />}
                    >
                      View JWT Token Details
                    </Button>

                  </VStack>
                </CardBody>
              </Card>
            )}

            {/* User Help Card - Show in UAT and Production */}
            {!isDevEnvironment && (
              <Card>
                <CardHeader>
                  <Heading size="md">Need Help?</Heading>
                </CardHeader>
                <CardBody>
                  <VStack spacing="4" align="stretch">
                    <Alert status="info" variant="subtle">
                      <AlertIcon />
                      <Box>
                        <AlertTitle>Common Issues</AlertTitle>
                        <AlertDescription>
                          If you can't see practices or providers
                          after the app updates, try refreshing your
                          login first.
                        </AlertDescription>
                      </Box>
                    </Alert>


                    <Text fontSize="sm" color="gray.600">
                      For technical support, contact your IT
                      department or the Denim Health support team.
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            )}
          </Stack>
        </GridItem>
      </Grid>

      {/* Full Token Modal - Only in QA */}
      {isDevEnvironment && (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>JWT Token Details (QA Only)</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb={6}>
              <VStack spacing="4" align="stretch">
                <Text fontSize="sm" color="gray.600">
                  This is the complete JWT token payload for debugging
                  purposes.
                </Text>

                <Box
                  bg="gray.50"
                  p="4"
                  borderRadius="md"
                  border="1px solid"
                  borderColor="gray.200"
                  maxH="400px"
                  overflow="auto"
                >
                  <Code fontSize="xs" whiteSpace="pre-wrap">
                    {JSON.stringify(tokenInfo, null, 2)}
                  </Code>
                </Box>

                <HStack justify="space-between">
                  <Button
                    size="sm"
                    colorScheme="blue"
                    variant="outline"
                    onClick={() =>
                      copyToClipboard(
                        JSON.stringify(tokenInfo, null, 2),
                        'JWT Payload'
                      )
                    }
                    leftIcon={<CopyIcon />}
                  >
                    Copy Payload
                  </Button>

                  <Button
                    size="sm"
                    colorScheme="gray"
                    variant="outline"
                    onClick={onClose}
                  >
                    Close
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>
      )}
    </Box>
  );
};

export default ProfilePage;
